"use client";

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { getImageUrl } from '@/lib/api';
import ChatAssistant from '@/components/Assistant/ChatAssistant';
import NotificationSystem from '@/components/NotificationSystem';
import {
    ChevronRight,
    ChevronLeft,
    ChevronDown,
    Menu,
    X,
    LogOut
} from 'lucide-react';
import { MASTER_MENU_ITEMS } from '@/constants/masterAdmin';

export default function MasterAdminDarkLayout({ user, logout, children }) {
    const pathname = usePathname();
    const router = useRouter();

    const [expandedMenu, setExpandedMenu] = useState(null);
    const [pathnameAtToggle, setPathnameAtToggle] = useState(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Auto-expandir menú padre si la ruta activa corresponde a un submenú
    const autoExpandedMenu = useMemo(() => {
        const activeParent = MASTER_MENU_ITEMS.find(item =>
            item.subItems?.some(sub => pathname === sub.href)
        );
        return activeParent ? activeParent.name : null;
    }, [pathname]);

    const effectiveExpandedMenu = pathnameAtToggle === pathname ? expandedMenu : autoExpandedMenu;

    const toggleMenu = (name) => {
        setPathnameAtToggle(pathname);
        setExpandedMenu(effectiveExpandedMenu === name ? null : name);
    };

    return (
        <div className="min-h-screen flex font-sans overflow-hidden relative bg-[#07060b] text-white">
            {/* Overlay para Móvil */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/70 z-[55] md:hidden backdrop-blur-sm animate-in fade-in duration-300"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            {/* Sidebar Fija Dark con Scroll Propio */}
            <aside className={`
                bg-[#0b0912] text-[#8e99a3] flex flex-col h-screen top-0 left-0 z-[60] shadow-2xl transition-all duration-300 ease-in-out
                fixed md:sticky md:translate-x-0 shrink-0 border-r border-white/5
                ${isSidebarCollapsed ? 'w-[80px]' : 'w-[260px]'}
                ${isMobileMenuOpen ? 'translate-x-0 w-[260px]' : 'max-md:-translate-x-full'}
            `}>
                {/* Cabecera del Sidebar con Marca Exclusiva de Super Administrador */}
                <div className={`flex flex-col items-center border-b border-white/10 bg-[#07060b] shrink-0 transition-all ${isSidebarCollapsed ? 'px-2 py-4' : 'px-6 py-5'}`}>
                    {isSidebarCollapsed ? (
                        <img src="/brand/rege-symbol-white.svg" alt="REGE" className="h-9 w-9" />
                    ) : (
                        <div className="flex w-full flex-col items-center gap-3 text-center animate-in fade-in duration-300">
                            <img src="/brand/rege-lockup-horizontal-white.svg" alt="REGE Proptech" className="h-7 w-auto" />
                            <span className="text-[10px] text-purple-300 font-bold uppercase tracking-[0.16em] px-2.5 py-0.5 rounded-full bg-purple-500/10 border border-purple-400/20">
                                Super Admin
                            </span>
                        </div>
                    )}
                </div>

                {/* Lista de Navegación Exclusiva */}
                <nav className="flex-grow overflow-y-auto scrollbar-none py-3">
                    {MASTER_MENU_ITEMS.map((item) => {
                        const hasSub = item.subItems && item.subItems.length > 0;
                        const isExpanded = effectiveExpandedMenu === item.name;
                        const isActive = item.href === '/dashboard/workflows'
                            ? pathname === '/dashboard/workflows'
                            : (pathname === item.href || (
                                item.href !== '/dashboard/master' &&
                                item.href !== '/dashboard' &&
                                pathname.startsWith(item.href) &&
                                !hasSub
                            ));

                        return (
                            <div key={item.name} className="flex flex-col mb-0.5">
                                {hasSub ? (
                                    <button
                                        onClick={() => toggleMenu(item.name)}
                                        className={`flex items-center justify-between px-4 py-3 text-[14px] font-medium transition-all w-full text-left cursor-pointer ${
                                            isExpanded
                                                ? 'bg-purple-500/10 text-white font-semibold'
                                                : 'text-[#8e99a3] hover:bg-white/5 hover:text-white'
                                        } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                                        title={isSidebarCollapsed ? item.name : ''}
                                    >
                                        <div className="flex items-center">
                                            <item.icon className={`w-4 h-4 ${isSidebarCollapsed ? '' : 'mr-3'} ${isExpanded ? 'text-purple-300' : 'text-gray-400'}`} />
                                            {!isSidebarCollapsed && <span className="tracking-tight">{item.name}</span>}
                                        </div>
                                        {!isSidebarCollapsed && (
                                            isExpanded ? <ChevronDown size={14} className="text-purple-300" /> : <ChevronRight size={14} className="opacity-40" />
                                        )}
                                    </button>
                                ) : (
                                    <Link
                                        href={item.href}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className={`flex items-center justify-between px-4 py-3 text-[14px] font-medium transition-all ${
                                            isActive
                                                ? 'bg-purple-500/10 text-purple-300 font-semibold border-r-2 border-purple-400'
                                                : 'text-[#8e99a3] hover:bg-white/5 hover:text-white'
                                        } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                                        title={isSidebarCollapsed ? item.name : ''}
                                    >
                                        <div className="flex items-center">
                                            <item.icon className={`w-4 h-4 ${isSidebarCollapsed ? '' : 'mr-3'} ${isActive ? 'text-purple-300' : 'text-gray-400'}`} />
                                            {!isSidebarCollapsed && <span className="tracking-tight">{item.name}</span>}
                                        </div>
                                    </Link>
                                )}

                                {/* Submenu Items */}
                                {hasSub && isExpanded && !isSidebarCollapsed && (
                                    <div className="bg-[#07060b]/80 py-1 border-y border-white/5 animate-in slide-in-from-top-1 duration-150">
                                        {item.subItems.map((sub) => {
                                            const isSubActive = pathname === sub.href;
                                            return (
                                                <Link
                                                    key={sub.name}
                                                    href={sub.href}
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                    className={`flex items-center px-10 py-2.5 text-[13px] transition-all ${
                                                        isSubActive
                                                            ? 'bg-purple-500/10 text-purple-300 font-semibold border-r-2 border-purple-400'
                                                            : 'text-[#7f8c97] hover:text-white hover:bg-white/5'
                                                    }`}
                                                >
                                                    {sub.icon && <sub.icon className="w-3.5 h-3.5 mr-2.5 opacity-70" />}
                                                    <span className="tracking-tight">{sub.name}</span>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </nav>

                {/* Botón Colapsar Sidebar en Desktop */}
                <button
                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                    className="hidden md:flex p-4 border-t border-white/5 hover:bg-white/5 transition-colors items-center justify-center text-white/40 hover:text-white mt-auto cursor-pointer"
                >
                    {isSidebarCollapsed ? <Menu size={20} /> : <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest"><ChevronLeft size={16} /> Colapsar</div>}
                </button>
            </aside>

            {/* Área de Contenido Principal - Dark Scrollable */}
            <div className="flex-grow flex flex-col h-screen overflow-y-auto relative custom-scrollbar-admin bg-[#07060b]">
                {/* Header Superior Topbar Exclusivo Dark */}
                <header className="h-[75px] flex items-center justify-between px-4 md:px-6 shrink-0 sticky top-0 z-40 backdrop-blur-md bg-[#0b0912] border-b border-white/5 shadow-none">
                    {/* Botones de Navegación e Info de Super Admin */}
                    <div className="flex items-center gap-3 md:gap-6">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="p-2 text-white/60 hover:bg-white/5 rounded-lg transition-colors border border-white/10 md:hidden cursor-pointer"
                        >
                            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>

                        <button
                            onClick={() => router.back()}
                            className="p-2 rounded-lg transition-colors hidden md:flex text-white/40 hover:bg-white/5 border border-white/10 cursor-pointer"
                            title="Regresar"
                        >
                            <ChevronLeft size={18} />
                        </button>

                        <div className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-white/10 shadow-sm bg-[#07060b]">
                                <img
                                    src={user?.profile_picture_url ? getImageUrl(user.profile_picture_url) : 'https://www.gravatar.com/avatar/000?d=mp'}
                                    className="w-full h-full object-cover"
                                    alt="Super Admin"
                                    onError={(e) => { e.target.src = "https://www.gravatar.com/avatar/000?d=mp"; }}
                                />
                            </div>
                            <div className="flex flex-col justify-center max-w-[150px] sm:max-w-none">
                                <p className="text-[13px] font-black leading-none mb-1 truncate text-white">
                                    {user?.full_name || user?.username || 'Super Admin'}
                                </p>
                                <p className="text-[11px] font-bold uppercase tracking-tighter hidden sm:block text-white/40">
                                    {user?.email} • <span className="text-purple-300 font-bold">SUPERADMIN</span>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Acciones Derecha: Notificaciones y Cerrar Sesión */}
                    <div className="flex items-center gap-4">
                        <NotificationSystem />

                        <div className="h-8 w-[1px] bg-white/10 mx-2"></div>

                        <button
                            onClick={logout}
                            className="p-3 transition-all rounded-xl group text-white/40 hover:text-red-400 hover:bg-red-400/10 cursor-pointer"
                            title="Cerrar Sesión"
                        >
                            <LogOut size={22} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                </header>

                {/* Lienzo de Contenido Dark: `master-dark-scope` adapta al dark theme las páginas
                    compartidas con el CRM (Perfil, Ayuda, Plantillas) que fueron diseñadas en claro. */}
                <main className="master-dark-scope w-full mx-auto flex-grow flex flex-col max-w-[1700px] p-6 md:p-8 text-white bg-[#07060b]">
                    {children}
                </main>

                {/* Asistente Baly IA Integrado */}
                <ChatAssistant />
            </div>
        </div>
    );
}
